import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ExternalLink, Sparkles, Pencil } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useSentryProject } from "@/hooks/use-sentry-project";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/_authenticated/projects/$id/workspace")({
  head: () => ({
    meta: [
      { title: "Project workspace board — Lumvio" },
      { name: "description", content: "Collaborate on a shared Miro whiteboard for this project." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: WorkspacePage,
});

interface ProjectRow {
  id: string;
  freelancer_id: string;
  client_email: string;
  title: string;
  miro_board_url: string | null;
}

function toMiroEmbed(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const url = raw.trim();
  if (!url) return null;
  if (/^https:\/\/miro\.com\/app\/live-embed\//i.test(url)) return url;
  const m = url.match(/miro\.com\/app\/(?:board|live-embed)\/([A-Za-z0-9_=-]+)/i);
  if (!m) return null;
  const id = m[1];
  return `https://miro.com/app/live-embed/${id}/?embedMode=view_only_without_ui&autoplay=true`;
}

function WorkspacePage() {
  const { id } = useParams({ from: "/_authenticated/projects/$id/workspace" });
  useSentryProject(id);
  const { user } = useAuth();
  const qc = useQueryClient();
  const { t } = useTranslation();

  const { data: project, isLoading } = useQuery({
    queryKey: ["project-workspace", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id, freelancer_id, client_email, title, miro_board_url")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return data as ProjectRow | null;
    },
  });

  const isOwner = !!(user && project && user.id === project.freelancer_id);
  const embedUrl = toMiroEmbed(project?.miro_board_url);

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  const save = useMutation({
    mutationFn: async (raw: string) => {
      const trimmed = raw.trim();
      if (trimmed && !/^https?:\/\/(www\.)?miro\.com\//i.test(trimmed)) {
        throw new Error(t("workspace.invalidUrl"));
      }
      const { error } = await supabase
        .from("projects")
        .update({ miro_board_url: trimmed || null })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("workspace.updated"));
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["project-workspace", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return <div className="py-24 text-center text-muted-foreground">{t("workspace.loading")}</div>;
  }

  if (!project) {
    return (
      <div className="py-24 text-center space-y-3">
        <p className="text-muted-foreground">{t("workspace.notAvailable")}</p>
        <Button asChild variant="link"><Link to="/dashboard">{t("common.backToDashboard")}</Link></Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-[fade-in_.4s_ease-out]">
      <header className="flex flex-col gap-4 border-b border-border pb-6 md:flex-row md:items-end md:justify-between">
        <div className="space-y-2">
          <Link
            to="/projects/$id"
            params={{ id }}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" /> {t("workspace.backToProject")}
          </Link>
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-primary" />
            <h1 className="font-serif text-3xl font-semibold tracking-tight md:text-4xl">
              {project.title} — {t("workspace.titleSuffix")}
            </h1>
          </div>
          <p className="max-w-2xl text-sm text-muted-foreground">
            {t("workspace.description")}
          </p>
        </div>
        {isOwner && (
          <div className="flex items-center gap-2">
            {embedUrl && (
              <a
                href={project.miro_board_url!}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-border bg-background px-4 text-sm font-medium hover:bg-accent"
              >
                <ExternalLink className="h-4 w-4" /> {t("workspace.openInMiro")}
              </a>
            )}
            <Button
              variant={embedUrl ? "outline" : "default"}
              onClick={() => {
                setValue(project.miro_board_url ?? "");
                setEditing((v) => !v);
              }}
            >
              <Pencil className="me-1.5 h-4 w-4" />
              {embedUrl ? t("workspace.changeBoard") : t("workspace.attachBoard")}
            </Button>
          </div>
        )}
      </header>

      {isOwner && editing && (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-card)] space-y-3">
          <label className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {t("workspace.shareUrlLabel")}
          </label>
          <Input
            type="url"
            placeholder={t("workspace.urlPlaceholder")}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">{t("workspace.shareHint")}</p>
          <div className="flex gap-2">
            <Button onClick={() => save.mutate(value)} disabled={save.isPending}>
              {save.isPending ? t("common.saving") : t("workspace.saveBoard")}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>{t("common.cancel")}</Button>
            {project.miro_board_url && (
              <Button
                variant="ghost"
                className="text-destructive"
                onClick={() => save.mutate("")}
                disabled={save.isPending}
              >
                {t("common.remove")}
              </Button>
            )}
          </div>
        </div>
      )}

      {embedUrl ? (
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-card)]">
          <iframe
            key={embedUrl}
            src={embedUrl}
            title={t("workspace.iframeTitle", { title: project.title })}
            className="h-[78vh] w-full"
            frameBorder={0}
            allow="fullscreen; clipboard-read; clipboard-write"
            allowFullScreen
          />
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-border bg-card/40 p-12 text-center">
          <Sparkles className="mx-auto h-8 w-8 text-muted-foreground/60" />
          <h2 className="mt-3 font-serif text-xl text-foreground">{t("workspace.noBoardTitle")}</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            {isOwner ? t("workspace.ownerNoBoard") : t("workspace.clientNoBoard")}
          </p>
        </div>
      )}
    </div>
  );
}
